import type { FC } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import './react-datepicker.css';
import { useLocale } from '@pflegenavi/frontend/localization';
import { deAT, enGB } from 'date-fns/locale';
import { InputLabel, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { endOfDay, startOfDay } from 'date-fns';

registerLocale('de-AT', deAT);
registerLocale('en-GB', enGB);

const DATE_FORMAT_BY_LOCALE: Record<string, string> = {
  'de-AT': 'dd.MM.yyyy',
  'en-GB': 'dd/MM/yyyy',
};

interface DateRangeProps {
  range?: {
    from?: Date;
    to?: Date;
  };
  onChange: (value: { from?: Date; to?: Date }) => void;
  label?: string;
  // Renders the calendar in place instead of an input with a popper. Use
  // inside popovers, where a nested popper gets clipped.
  inline?: boolean;
}
export const DateRange: FC<DateRangeProps> = ({
  label,
  range,
  onChange,
  inline,
}) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const dateFormat = DATE_FORMAT_BY_LOCALE[locale];
  return (
    <Stack
      // The stylesheet stacks and stretches the months as a flex column, which
      // only works inside the popper. Inline, lay them out side by side.
      sx={{
        gap: 0,
        ...(inline && {
          '& .react-datepicker': { flexDirection: 'row' },
          '& .react-datepicker__month-container': { boxShadow: 'none' },
        }),
      }}
    >
      {label && <InputLabel>{label}</InputLabel>}
      <DatePicker
        formatWeekDay={(day) => day.slice(0, 1)}
        placeholderText={t('table.filters.date-range.placeholder')}
        dateFormat={dateFormat}
        onKeyDown={(e) => e.preventDefault()}
        isClearable={!inline}
        inline={inline}
        customInput={<TextField />}
        locale={locale}
        selected={range?.from ?? null}
        startDate={range?.from ?? null}
        endDate={range?.to ?? null}
        monthsShown={2}
        selectsRange
        onChange={(date) => {
          const startDate = date[0] ?? undefined;
          const endDate = date[1] ?? undefined;

          onChange({
            from: startDate ? startOfDay(startDate) : undefined,
            to: endDate ? endOfDay(endDate) : undefined,
          });
        }}
      />
    </Stack>
  );
};
